import "zod/compile";
import { z } from "zod";
import { workbookCellSchema } from "./workbooks";
import {
  ReportAggregateFunctionSchema,
  TABLE_COMPONENT_MAX_ROWS,
} from "./queryReport";
import { reportFiltersSchema } from "./reportFilters";

/**
 * The Client row limit, the breakpoint between a Report's two data paths: a
 * Result of at most this many rows, serialized in at most
 * WORKBOOK_REPORT_CLIENT_BYTES, is sent to the browser whole and charted there,
 * as the query builder's reports are; a larger one is charted from Server
 * aggregation instead.
 *
 * Workbooks are spreadsheet-sized work, not a warehouse (big data belongs in
 * Snowflake or Databricks): most Results are a few hundred to a few thousand
 * rows, and 10,000 rows of a typical 10 to 30 column Result is 1 to 4 MB of
 * JSON, which a browser parses in well under a second and charts without
 * jank. The byte cap catches wide or long-text Results that fit the row limit
 * but not the budget.
 */
export const WORKBOOK_REPORT_CLIENT_ROW_LIMIT = 10_000;
export const WORKBOOK_REPORT_CLIENT_BYTES = 4 * 1024 * 1024;
/** The most groups one Server aggregation returns; more marks it truncated. */
export const WORKBOOK_REPORT_MAX_GROUPS = 5_000;
/** The most rows a Report reads when it asks for a Result's leading rows: a Table's. */
export const WORKBOOK_REPORT_MAX_LEADING_ROWS = TABLE_COMPONENT_MAX_ROWS;

const reportTarget = {
  workbookId: z.uuid(),
  /** A completed Run holding a Result for the Worksheet. */
  runId: z.uuid(),
  sheetId: z.uuid(),
  /**
   * The Report filters in effect, date presets already resolved to dates:
   * only the rows passing those on the Result's columns are read.
   */
  filters: reportFiltersSchema.optional(),
};

export const workbookResultRowsInputSchema = z.object({
  ...reportTarget,
  /**
   * Only the Result's first `limit` rows, whatever its size. Without it, a
   * Result past the Client row limit returns no rows.
   */
  limit: z
    .number()
    .int()
    .positive()
    .max(WORKBOOK_REPORT_MAX_LEADING_ROWS)
    .optional(),
});

export type WorkbookResultRowsInput = z.infer<
  typeof workbookResultRowsInputSchema
>;

/**
 * A Result's rows for a Report: every row (`rows`) when within the Client row
 * limit or `limit` was asked for, else none (`server`), so the Report asks for
 * Server aggregation.
 */
export const workbookResultRowsSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("rows"),
    columns: z.array(z.string()),
    rows: z.array(z.array(workbookCellSchema)),
    rowCount: z.number().int().nonnegative(),
  }),
  z.object({
    kind: z.literal("server"),
    columns: z.array(z.string()),
    rowCount: z.number().int().nonnegative(),
  }),
]);
export type WorkbookResultRows = z.infer<typeof workbookResultRowsSchema>;

export const workbookAggregateFunctionSchema = ReportAggregateFunctionSchema;
export type WorkbookAggregateFunction = z.infer<
  typeof workbookAggregateFunctionSchema
>;

/**
 * A Server aggregation: a Result's rows grouped by `groupBy`, with one
 * aggregate per measure, each named for its column. Error cells read as
 * Empty; sum and avg read a non-Number measure as a Number where it is one.
 * Groups come in first-appearance order, or, with `limit`, the top (or, with
 * `order: "asc"`, bottom) `limit` by the first measure.
 */
export const workbookAggregationSchema = z
  .object({
    groupBy: z.array(z.string().min(1)).max(4),
    measures: z
      .array(
        z.object({
          column: z.string().min(1),
          fn: workbookAggregateFunctionSchema,
        }),
      )
      .max(8),
    limit: z
      .number()
      .int()
      .positive()
      .max(WORKBOOK_REPORT_MAX_GROUPS)
      .optional(),
    /** With `limit`: the top (`desc`) or bottom (`asc`) groups by the first measure. */
    order: z.enum(["desc", "asc"]).optional(),
    /**
     * Groups in ascending order of their values (numbers, then dates, then
     * text) instead of the order they first appear in: a line chart's x axis.
     * Without `limit` only; with it, the groups are the top or bottom ones.
     */
    sortGroups: z.boolean().optional(),
  })
  .refine(
    (aggregation) =>
      aggregation.groupBy.length + aggregation.measures.length > 0,
    "Group by a column or aggregate one.",
  )
  .refine((aggregation) => {
    const names = [
      ...aggregation.groupBy,
      ...aggregation.measures.map((measure) => measure.column),
    ].map((name) => name.toLowerCase());
    return new Set(names).size === names.length;
  }, "Each column can be grouped by or aggregated once.");
export type WorkbookAggregation = z.infer<typeof workbookAggregationSchema>;

export const workbookAggregateInputSchema = z.object({
  ...reportTarget,
  aggregation: workbookAggregationSchema,
});

export type WorkbookAggregateInput = z.infer<
  typeof workbookAggregateInputSchema
>;

/** One aggregated row per group: the `groupBy` values, then each measure's aggregate. */
export const workbookAggregateResultSchema = z.object({
  columns: z.array(z.string()),
  rows: z.array(
    z.array(z.union([z.string(), z.number().finite(), z.boolean(), z.null()])),
  ),
  /** The Result's rows the groups were computed from. */
  rowCount: z.number().int().nonnegative(),
  /** More groups exist than `rows` holds. */
  truncated: z.boolean(),
});
export type WorkbookAggregateResult = z.infer<
  typeof workbookAggregateResultSchema
>;

/** The most requests one Report data batch carries. */
export const WORKBOOK_REPORT_DATA_MAX_REQUESTS = 100;

const reportDataTarget = {
  runId: reportTarget.runId,
  sheetId: reportTarget.sheetId,
  filters: reportTarget.filters,
};
/** One Report component's read in a Report data batch: its Server aggregation, or its leading rows. */
export const workbookReportDataRequestSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("aggregate"),
    ...reportDataTarget,
    aggregation: workbookAggregationSchema,
  }),
  z.object({
    kind: z.literal("rows"),
    ...reportDataTarget,
    limit: workbookResultRowsInputSchema.shape.limit,
  }),
]);
export type WorkbookReportDataRequest = z.infer<
  typeof workbookReportDataRequestSchema
>;

/**
 * A Report's reads at once: the server reads each Result once and aggregates
 * it once for every chart that charts it, instead of once per chart.
 */
export const workbookReportDataInputSchema = z.object({
  workbookId: reportTarget.workbookId,
  requests: z
    .array(workbookReportDataRequestSchema)
    .min(1)
    .max(WORKBOOK_REPORT_DATA_MAX_REQUESTS),
});
export type WorkbookReportDataInput = z.infer<
  typeof workbookReportDataInputSchema
>;

/** Each request's answer, in request order; one request failing fails only its own. */
export const workbookReportDataSchema = z.array(
  z.discriminatedUnion("kind", [
    z.object({
      kind: z.literal("aggregate"),
      result: workbookAggregateResultSchema,
    }),
    z.object({ kind: z.literal("rows"), result: workbookResultRowsSchema }),
    z.object({ kind: z.literal("error"), message: z.string() }),
  ]),
);
export type WorkbookReportData = z.infer<typeof workbookReportDataSchema>;
